import React from "react";
import { Anchor, Compass, ShieldCheck, Waves, Target } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { CtaBand } from "@/components/CtaBand";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { BRAND, REGIONS } from "@/data/content";

const VALUES = [
  { icon: ShieldCheck, title: "Discretion", text: "We treat your vessel and your privacy with the same care a trusted household manager would." },
  { icon: Target, title: "Precision", text: "Standardized checklists and timestamped documentation on every single visit — no guesswork." },
  { icon: Compass, title: "Local Knowledge", text: "Grounded in Southwest Florida's marinas, weather patterns, and seasonal rhythms." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About — Hussar Marine Concierge"
        description="Hussar Marine Concierge is an independent dockside vessel oversight service and a division of Hussar Holdings LLC, serving Southwest Florida."
      />
      <PageHero
        eyebrow="About HMC"
        title="Vigilant stewardship for vessels left at the dock"
        subtitle="A division of Hussar Holdings LLC, built for owners who want their boat watched with precision and discretion."
        image="https://images.unsplash.com/photo-1544119154-ef19cd315b7f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
      />

      <section className="py-20 lg:py-28 bg-white dark:bg-navy-900">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="eyebrow text-gold-600 dark:text-gold-400 mb-4">Our Story</p>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-navy-900 dark:text-white">
              Built around a simple promise
            </h2>
            <div className="mt-5 space-y-4 text-slate-600 dark:text-slate-300 leading-relaxed">
              <p>
                Hussar Marine Concierge was created to solve a quiet worry shared by
                seasonal and absentee boat owners across Southwest Florida: what is
                happening to my vessel while I'm away?
              </p>
              <p>
                As a division of {BRAND.parent}, HMC brings a standard of care and
                attention to detail — mirrored in our signature white and gold livery —
                to the simple, essential work of keeping a watchful eye on your boat.
              </p>
              <p>
                We don't operate your vessel or perform mechanical work. Instead, we
                provide consistent dockside oversight, clear documentation, and
                coordination with the licensed professionals you already trust.
              </p>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden h-[460px] shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1745167554647-a02b5011140b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                  alt="Luxury motor yacht on open water"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-3 border border-gold-500/30 shadow-xl hidden sm:block">
                <img src="/hmc-logo-full.png" alt="Hussar Marine Concierge" className="h-28 w-28 object-contain" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-ivory dark:bg-navy-800">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <Reveal>
            <p className="eyebrow text-gold-600 dark:text-gold-400 mb-4 text-center">What Guides Us</p>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-navy-900 dark:text-white text-center">
              Standards you can feel on every visit
            </h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-3 gap-7">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 110}>
                <div className="card-lift h-full bg-white dark:bg-navy-900 border border-gold-500/20 rounded-2xl p-8 text-center">
                  <div className="h-14 w-14 mx-auto rounded-2xl bg-navy-900 dark:bg-gold-500/10 flex items-center justify-center">
                    <v.icon className="h-7 w-7 text-gold-500" />
                  </div>
                  <h3 className="mt-5 font-serif text-xl text-navy-900 dark:text-white">{v.title}</h3>
                  <p className="mt-3 text-slate-600 dark:text-slate-300">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white dark:bg-navy-900">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 text-center">
          <Reveal>
            <Waves className="h-10 w-10 text-gold-500 mx-auto" />
            <p className="eyebrow text-gold-600 dark:text-gold-400 mt-5 mb-4">Where We Work</p>
            <h2 className="text-3xl font-serif font-bold text-navy-900 dark:text-white">
              Proposed coverage across {BRAND.serviceArea}
            </h2>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {REGIONS.map((r) => (
                <span key={r} className="rounded-full border border-gold-500/30 text-navy-900 dark:text-slate-200 px-5 py-2 text-sm font-medium">
                  {r}
                </span>
              ))}
            </div>
            <p className="mt-6 text-sm text-slate-600 dark:text-slate-400 max-w-xl mx-auto">
              Coverage is being rolled out progressively and is subject to availability
              and marina authorization at each location.
            </p>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
